/**
 * V70 게임대회 — 공용 오픈 기간 유틸
 *
 * 사용자 지시 반영:
 *   - §A.7 기간 미설정(null) 시 하위 호환성 — 기존처럼 'during' 간주
 *   - §A.8 KST 변환 중복 보정 금지 — datetime-local 입력은 로컬 시간대로 해석,
 *          서버 timestamp 와 비교 시 raw millis 로 처리
 *   - §A.1 enabled=false → 'disabled' 반환 (system_admin 공식 플레이 불가 상태 포함)
 *
 * 리더보드 공개 정책 (사용자 지시 §A.3~6):
 *   before   — COMING SOON 표시, 실제 순위 비공개
 *   during   — 공개
 *   after    — 공개 (최종 순위)
 *   disabled — 비공개 (리더보드 자체 숨김)
 */
export type GamePeriodState = 'before' | 'during' | 'after' | 'disabled';

export interface GamePeriodInput {
  enabled: boolean;
  openStartAt: number | null;
  openEndAt: number | null;
  /** 서버 응답 timestamp 가 있으면 그 값, 없으면 Date.now() (FE 데모 fallback) */
  nowMs?: number;
}

export interface GamePeriodResult {
  state: GamePeriodState;
  startAt: number | null;
  endAt: number | null;
  /** D-day 계산용 밀리초 차이 */
  diffMs: number | null;
  /** 플레이 가능 여부 (`during` 만 true) */
  canPlay: boolean;
  /** 리더보드 공개 여부 (사용자 지시 §A.3~6) */
  leaderboardVisible: boolean;
  /**
   * 상시 오픈 모드 플래그 — enabled && startAt=null && endAt=null.
   * UI 가 "상시 오픈" 뱃지로 분기할 때 사용. state 는 하위 호환을 위해 'during' 유지.
   */
  isAlwaysOpen: boolean;
  reason: string | null;
}

export function computeGamePeriod(input: GamePeriodInput): GamePeriodResult {
  const now = input.nowMs ?? Date.now();
  if (!input.enabled) {
    return {
      state: 'disabled',
      startAt: input.openStartAt,
      endAt: input.openEndAt,
      diffMs: null,
      canPlay: false,
      leaderboardVisible: false,
      isAlwaysOpen: false,
      reason: '관리자가 게임을 공개하지 않았습니다.',
    };
  }
  // 하위 호환성: 기간 미설정 → 상시 오픈 (사용자 지시 §A.7)
  if (input.openStartAt == null && input.openEndAt == null) {
    return {
      state: 'during',
      startAt: null, endAt: null, diffMs: null,
      canPlay: true, leaderboardVisible: true,
      isAlwaysOpen: true,
      reason: null,
    };
  }
  if (input.openStartAt != null && now < input.openStartAt) {
    return {
      state: 'before',
      startAt: input.openStartAt, endAt: input.openEndAt,
      diffMs: input.openStartAt - now,
      canPlay: false,
      leaderboardVisible: false,  // 사용자 지시 §A.3: 오픈 전 리더보드 비공개
      isAlwaysOpen: false,
      reason: 'COMING SOON — 아직 오픈 전입니다.',
    };
  }
  if (input.openEndAt != null && now >= input.openEndAt) {
    return {
      state: 'after',
      startAt: input.openStartAt, endAt: input.openEndAt,
      diffMs: now - input.openEndAt,
      canPlay: false,
      leaderboardVisible: true,  // 사용자 지시 §A.5: 종료 후 공개
      isAlwaysOpen: false,
      reason: 'ENDED — 대회가 종료되었습니다.',
    };
  }
  return {
    state: 'during',
    startAt: input.openStartAt, endAt: input.openEndAt,
    diffMs: input.openEndAt != null ? input.openEndAt - now : null,
    canPlay: true, leaderboardVisible: true,
    isAlwaysOpen: false,
    reason: null,
  };
}

/**
 * datetime-local 입력(브라우저 로컬 시간) → UTC timestamp.
 *
 * `<input type="datetime-local">` 는 사용자가 입력한 시각을 **로컬 타임존 벽시계** 로
 * 저장한다. new Date("YYYY-MM-DDTHH:mm") 는 그 벽시계를 UTC 로 환산한다.
 *
 * 사용자 지시 §A.8: "KST 오프셋 중복 적용 금지".
 * 브라우저가 KST(Asia/Seoul) 라면 아무 보정 없이 그대로 사용한다.
 * 다른 timezone 에서 접속하면 getTime() 이 다르게 나오지만, 서버 timestamp 와의
 * 비교는 UTC 기준이므로 **문제 없음**. (운영자는 보통 KST 로컬에서 조작)
 */
export function datetimeLocalToTimestamp(value: string | null | undefined): number | null {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : null;
}

/** UTC timestamp → datetime-local string (로컬 시간대 벽시계). */
export function timestampToDatetimeLocal(ms: number | null): string {
  if (ms == null) return '';
  const d = new Date(ms);
  const tzOffsetMs = d.getTimezoneOffset() * 60_000;
  const localMs = ms - tzOffsetMs;
  return new Date(localMs).toISOString().slice(0, 16); // "YYYY-MM-DDTHH:mm"
}

/**
 * 남은 시간 포맷 (ISSUE-06).
 *
 * - 24시간 이상 → `D-N` (기존 D-day 유지)
 * - 1시간 ~ 24시간 미만 → `N시간`
 * - 1분 ~ 1시간 미만 → `N분`
 * - 양수지만 1분 미만 → `1분 미만`
 * - 0 또는 음수 → `종료`
 *
 * KST 벽시계는 호출 측에서 Date.now()·서버 timestamp 로 변환. 이 함수는 밀리초 차이만 포맷팅.
 */
export function formatDiff(diffMs: number | null): string {
  if (diffMs == null) return '';
  if (diffMs <= 0) return '종료';
  const abs = diffMs;
  if (abs >= 86_400_000) {
    // Math.ceil 로 23h 59m 는 D-1, 24h+ 는 D-N 처리 (사용자 지시 §예시: "2일 3시간 → D-3")
    const days = Math.ceil(abs / 86_400_000);
    return `D-${days}`;
  }
  if (abs >= 3_600_000) {
    const hours = Math.floor(abs / 3_600_000);
    return `${hours}시간`;
  }
  if (abs >= 60_000) {
    const minutes = Math.floor(abs / 60_000);
    return `${minutes}분`;
  }
  return '1분 미만';
}

/** 상태 → 뱃지 라벨/색상. */
export function periodBadge(state: GamePeriodState): { label: string; bg: string; fg: string } {
  switch (state) {
    case 'before':   return { label: 'COMING SOON', bg: '#dbeafe', fg: '#1d4ed8' };
    case 'during':   return { label: '진행 중',      bg: '#dcfce7', fg: '#166534' };
    case 'after':    return { label: 'ENDED',       bg: '#f1f5f9', fg: '#475569' };
    case 'disabled': return { label: '비공개',      bg: '#fef3c7', fg: '#92400e' };
  }
}

/** 상시 오픈 전용 뱃지 (사용자 지시 §A §B — 미설정 시 "상시 오픈"). */
export const ALWAYS_OPEN_BADGE = { label: '상시 오픈', bg: '#e0e7ff', fg: '#3730a3' };
